import { useState, useEffect, useCallback } from 'react';
import { Plus, Pencil, UserCheck, UserX, ShieldCheck, Users, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../services/api';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useAuth } from '../contexts/AuthContext';

const ROL_COLORS = [
  'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400 ring-amber-200/70 dark:ring-amber-500/30',
  'bg-sky-50 dark:bg-sky-500/10 text-sky-700 dark:text-sky-400 ring-sky-200/70 dark:ring-sky-500/30',
  'bg-violet-50 dark:bg-violet-500/10 text-violet-700 dark:text-violet-400 ring-violet-200/70 dark:ring-violet-500/30',
  'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 ring-emerald-200/70 dark:ring-emerald-500/30',
  'bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400 ring-rose-200/70 dark:ring-rose-500/30',
];

const fmtLogin = (v) => {
  if (!v) return 'Nunca';
  const d = new Date(v);
  return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
};

// Iniciales del nombre para el avatar
const initials = (nombre = '') =>
  nombre.trim().split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase();

// Colores de avatar por índice
const AVATAR_COLORS = [
  'bg-primary-100 dark:bg-primary-500/20 text-primary-700 dark:text-primary-300',
  'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
  'bg-rose-100 dark:bg-rose-500/20 text-rose-700 dark:text-rose-300',
  'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-300',
  'bg-sky-100 dark:bg-sky-500/20 text-sky-700 dark:text-sky-300',
  'bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300',
];

// ── Formulario ────────────────────────────────────────────────────────────
const emptyForm = { nombre: '', username: '', rol_id: '', password: '', activo: true };

function UsuarioForm({ form, onChange, isEdit, isSelf, roles }) {
  const setEv = (f) => (e) => onChange({ ...form, [f]: e.target.value });
  const set   = (f) => (v) => onChange({ ...form, [f]: v });

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label>Nombre completo *</label>
          <input
            className="input"
            placeholder="Juan García"
            value={form.nombre}
            onChange={setEv('nombre')}
          />
        </div>
        <div>
          <label>Usuario *</label>
          <input
            className="input font-mono"
            placeholder="jgarcia"
            value={form.username}
            onChange={setEv('username')}
            autoCapitalize="off"
            autoCorrect="off"
          />
        </div>
      </div>

      <div>
        <label>Rol *</label>
        <div className="grid grid-cols-2 gap-2">
          {roles.map((r, i) => (
            <button
              key={r.id}
              type="button"
              disabled={isEdit && isSelf}
              onClick={() => set('rol_id')(r.id)}
              className={`flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all disabled:opacity-40 disabled:cursor-not-allowed text-left ${
                form.rol_id === r.id
                  ? 'border-primary-500 bg-primary-50 dark:bg-primary-500/10 text-primary-700 dark:text-primary-400'
                  : 'border-slate-200 dark:border-slate-700 text-slate-500 dark:text-slate-400 hover:border-slate-300 dark:hover:border-slate-600'
              }`}
            >
              <ShieldCheck size={14} className="shrink-0" />
              <span className="truncate">{r.nombre}</span>
            </button>
          ))}
        </div>
        {isEdit && isSelf && (
          <p className="text-xs text-slate-400 mt-1">No podés cambiar tu propio rol.</p>
        )}
      </div>

      <div>
        <label>{isEdit ? 'Nueva contraseña' : 'Contraseña *'}</label>
        <input
          className="input"
          type="password"
          placeholder={isEdit ? 'Dejar en blanco para no cambiar' : 'Mínimo 6 caracteres'}
          value={form.password}
          onChange={setEv('password')}
          autoComplete="new-password"
        />
      </div>

      {isEdit && (
        <div className="flex items-center justify-between py-2 border-t border-slate-100 dark:border-slate-800">
          <div>
            <p className="text-[13px] font-semibold text-slate-700 dark:text-slate-300">Cuenta activa</p>
            <p className="text-xs text-slate-400 mt-0.5">
              {isSelf ? 'No podés desactivar tu propia cuenta.' : 'Los usuarios inactivos no pueden iniciar sesión.'}
            </p>
          </div>
          <button
            type="button"
            disabled={isSelf}
            onClick={() => set('activo')(!form.activo)}
            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
              form.activo ? 'bg-primary-500' : 'bg-slate-200 dark:bg-slate-700'
            }`}
          >
            <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
              form.activo ? 'translate-x-6' : 'translate-x-1'
            }`} />
          </button>
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────
export default function Usuarios() {
  const { user: self, can } = useAuth();

  const [lista,   setLista]   = useState([]);
  const [roles,   setRoles]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editId,    setEditId]    = useState(null);
  const [form,      setForm]      = useState(emptyForm);
  const [saving,    setSaving]    = useState(false);

  const [confirmToggleId, setConfirmToggleId] = useState(null);
  const [toggling,        setToggling]        = useState(false);

  const puedeGestionar = can('usuarios.gestionar');

  const fetchUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const [resU, resR] = await Promise.all([
        api.get('/usuarios/index.php'),
        api.get('/roles/index.php'),
      ]);
      setLista(resU.data);
      setRoles(resR.data.roles);
    } catch { toast.error('Error al cargar usuarios'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchUsuarios(); }, [fetchUsuarios]);

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setEditId(u.id);
    setForm({ nombre: u.nombre, username: u.username, rol_id: u.rol_id ?? '', password: '', activo: u.activo });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.nombre.trim())   { toast.error('El nombre es requerido'); return; }
    if (!form.username.trim()) { toast.error('El usuario es requerido'); return; }
    if (!form.rol_id)          { toast.error('El rol es requerido'); return; }
    if (!editId && !form.password) { toast.error('La contraseña es requerida'); return; }
    if (form.password && form.password.length < 6) { toast.error('La contraseña debe tener al menos 6 caracteres'); return; }

    setSaving(true);
    try {
      const payload = {
        nombre:   form.nombre.trim(),
        username: form.username.trim(),
        rol_id:   form.rol_id,
        activo:   form.activo,
      };
      if (form.password) payload.password = form.password;

      if (editId) {
        await api.put(`/usuarios/index.php?id=${editId}`, payload);
        toast.success('Usuario actualizado');
      } else {
        await api.post('/usuarios/index.php', payload);
        toast.success('Usuario creado');
      }
      setModalOpen(false);
      fetchUsuarios();
    } catch (err) {
      toast.error(err?.message || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const handleToggleActivo = async () => {
    const u = lista.find(x => x.id === confirmToggleId);
    if (!u) return;
    setToggling(true);
    try {
      await api.put(`/usuarios/index.php?id=${u.id}`, {
        nombre:   u.nombre,
        username: u.username,
        rol_id:   u.rol_id,
        activo:   !u.activo,
      });
      toast.success(u.activo ? 'Usuario desactivado' : 'Usuario activado');
      setConfirmToggleId(null);
      fetchUsuarios();
    } catch (err) {
      toast.error(err?.message || 'Error al cambiar estado');
    } finally { setToggling(false); }
  };

  // Filtro por búsqueda
  const filtrada = lista.filter(u =>
    !search ||
    u.nombre.toLowerCase().includes(search.toLowerCase()) ||
    u.username.toLowerCase().includes(search.toLowerCase())
  );

  // Stats
  const totalActivos = lista.filter(u => u.activo).length;
  const totalAdmins  = lista.filter(u => u.rol_nombre === 'Administrador').length;

  return (
    <div className="space-y-5">

      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Usuarios</h1>
        {puedeGestionar && (
          <button className="btn-primary" onClick={openCreate}>
            <Plus size={18} /> Nuevo usuario
          </button>
        )}
      </div>

      {/* Stats */}
      {!loading && lista.length > 0 && (
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: 'Total',    value: lista.length,   icon: Users,      color: 'text-primary-500 dark:text-primary-400', bg: 'bg-primary-50 dark:bg-primary-500/10' },
            { label: 'Activos',  value: totalActivos,   icon: UserCheck,  color: 'text-emerald-500',                       bg: 'bg-emerald-50 dark:bg-emerald-500/10' },
            { label: 'Admins',   value: totalAdmins,    icon: ShieldCheck, color: 'text-amber-500',                        bg: 'bg-amber-50 dark:bg-amber-500/10' },
          ].map(({ label, value, icon: Icon, color, bg }) => (
            <div key={label} className="card-sm dark:bg-slate-800 flex items-center gap-3">
              <div className={`w-9 h-9 rounded-xl ${bg} flex items-center justify-center shrink-0`}>
                <Icon size={16} className={color} />
              </div>
              <div>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider">{label}</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white leading-tight">{value}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Buscador */}
      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          className="input pl-8"
          placeholder="Buscar por nombre o usuario..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Tabla */}
      <div className="card !p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-2 border-primary-200 border-t-primary-500 rounded-full animate-spin" />
          </div>
        ) : filtrada.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-slate-300 dark:text-slate-700">
            <Users size={48} strokeWidth={1.5} />
            <p className="text-sm mt-3 text-slate-400">
              {search ? 'Sin resultados para la búsqueda' : 'No hay usuarios registrados'}
            </p>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="table">
              <thead>
                <tr>
                  <th>Usuario</th>
                  <th>Login</th>
                  <th>Rol</th>
                  <th>Estado</th>
                  <th>Último ingreso</th>
                  {puedeGestionar && <th></th>}
                </tr>
              </thead>
              <tbody>
                {filtrada.map((u, i) => {
                  const isSelf   = u.id === self?.id;
                  const avatarCl = AVATAR_COLORS[i % AVATAR_COLORS.length];
                  const rolColor = ROL_COLORS[(roles.findIndex(r => r.id === u.rol_id)) % ROL_COLORS.length] ?? 'bg-slate-100 text-slate-500';
                  return (
                    <tr key={u.id} className={!u.activo ? 'opacity-50' : ''}>

                      {/* Nombre */}
                      <td>
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0 select-none ${avatarCl}`}>
                            {initials(u.nombre)}
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900 dark:text-white text-sm leading-none">
                              {u.nombre}
                              {isSelf && (
                                <span className="ml-2 text-[10px] font-semibold text-primary-400 bg-primary-50 dark:bg-primary-500/10 px-1.5 py-0.5 rounded-full">
                                  Vos
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </td>

                      {/* Username */}
                      <td className="font-mono text-sm text-slate-500 dark:text-slate-400">{u.username}</td>

                      {/* Rol */}
                      <td>
                        <span className={`inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full ring-1 ${rolColor}`}>
                          <ShieldCheck size={10} />
                          {u.rol_nombre}
                        </span>
                      </td>

                      {/* Estado */}
                      <td>
                        {u.activo ? (
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                            Activo
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-400">
                            <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-600" />
                            Inactivo
                          </span>
                        )}
                      </td>

                      {/* Último login */}
                      <td className="text-slate-400 text-sm whitespace-nowrap">{fmtLogin(u.ultimo_login)}</td>

                      {/* Acciones */}
                      {puedeGestionar && (
                        <td>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => openEdit(u)}
                              className="p-1.5 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 transition-colors"
                              title="Editar"
                            >
                              <Pencil size={14} />
                            </button>
                            {!isSelf && (
                              <button
                                onClick={() => setConfirmToggleId(u.id)}
                                className={`p-1.5 rounded-lg transition-colors ${
                                  u.activo
                                    ? 'hover:bg-rose-50 dark:hover:bg-rose-500/10 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400'
                                    : 'hover:bg-emerald-50 dark:hover:bg-emerald-500/10 text-slate-400 hover:text-emerald-600 dark:hover:text-emerald-400'
                                }`}
                                title={u.activo ? 'Desactivar' : 'Activar'}
                              >
                                {u.activo ? <UserX size={14} /> : <UserCheck size={14} />}
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal crear / editar */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editId ? 'Editar usuario' : 'Nuevo usuario'}
        footer={
          <>
            <button className="btn-secondary" onClick={() => setModalOpen(false)} disabled={saving}>Cancelar</button>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Guardando...' : editId ? 'Guardar cambios' : 'Crear usuario'}
            </button>
          </>
        }
      >
        <UsuarioForm
          form={form}
          onChange={setForm}
          isEdit={!!editId}
          isSelf={editId === self?.id}
          roles={roles}
        />
      </Modal>

      {/* Confirm: activar / desactivar */}
      {(() => {
        const u = lista.find(x => x.id === confirmToggleId);
        return (
          <ConfirmDialog
            isOpen={!!confirmToggleId}
            onClose={() => setConfirmToggleId(null)}
            onConfirm={handleToggleActivo}
            loading={toggling}
            title={u?.activo ? 'Desactivar usuario' : 'Activar usuario'}
            message={u?.activo
              ? `¿Desactivar a ${u?.nombre}? No podrá iniciar sesión hasta que se reactive.`
              : `¿Activar a ${u?.nombre}? Podrá iniciar sesión nuevamente.`
            }
          />
        );
      })()}
    </div>
  );
}
